/* globals App */

import { defaults, isElement } from 'lodash';
import flatpickr from 'flatpickr';
import Sortable from 'sortablejs';
import { getOptionsFromElement, htmlToElement, parseDataAttr } from './utils';
import axios from 'axios';

export class Fields {
  selectors = {
    datePicker: '[data-datepicker]',
    dateSelect: '[data-dateselect]',
    matrix: '[data-matrix]',
    table: '[data-table]',
    uploadit: '.uploadit--uploader',
    packageContents: '.package-contents-field',
  };

  defaults = {
    // https://github.com/flatpickr/flatpickr/blob/master/src/types/options.ts
    datePicker: {
      dateFormat: 'Y-m-d 07:00:00',
      enableTime: false,
      minDate: 'today',
      altInput: true,
      altFormat: 'J M Y',
      shorthandCurrentMonth: true,
      mode: 'single',
    },
    dateSelect: {
      numberOfYears: 100,
      endYear: null,
    },
    matrix: {},
    table: {},
    addresses: {},
  };

  constructor() {
    this.initDatePickers();
    this.initDateSelects();
    this.initMatrix();
    this.initTables();
    this.initAddressChoice();
    this.initCheckboxSync();
    this.initUploadit();
    this.initPackageContents(document);
    console.log('Fields initialised');
  }

  initUploadit() {
    if (document.querySelectorAll(this.selectors.uploadit).length) {
      import(/* webpackChunkName: "uploadit" */ './uploadit').then((chunk) => {
        document.querySelectorAll(this.selectors.uploadit).forEach((elem) => {
          new chunk.Uploadit(elem);
        });
      });
    }
  }

  initPackageContents(container) {
    if (container.querySelectorAll(this.selectors.packageContents).length) {
      import(/* webpackChunkName: "packageContents" */ './packageContents').then((chunk) => {
        document.querySelectorAll(this.selectors.packageContents).forEach((elem) => {
          new chunk.PackageContents(elem);
        });
      });
    }
  }

  initDatePicker(input, options) {
    let settings = defaults(getOptionsFromElement(input), options, this.defaults.datePicker.options);
    if (settings.mode == 'range') {
      let holder = input.parentNode;
      let fromInput = holder.querySelector('[data-date-from]');
      let toInput = holder.querySelector('[data-date-to]');
      settings.onChange = (selectedDates, dateStr, instance) => {
        let from = selectedDates[0] || false;
        let to = selectedDates[1] || false;
        fromInput.value = from && from != '' ? instance.formatDate(from, instance.config.dateFormat) : '';
        toInput.value = to && to != '' ? instance.formatDate(to, instance.config.dateFormat) : '';
      };
    }
    new flatpickr(input, settings);
  }

  initDateSelect(field, options) {
    let settings = defaults(getOptionsFromElement(field), options, this.defaults.dateSelect.options);

    let endYear = settings.endYear ? settings.endYear : new Date().getFullYear();

    let dateInput = field.querySelector('[data-input]');

    let yearSelect = field.querySelector('[data-year]');
    let monthSelect = field.querySelector('[data-month]');
    let daySelect = field.querySelector('[data-day]');

    let activeYearSelection = yearSelect.getAttribute('data-year');
    let activeMonthSelection = monthSelect.getAttribute('data-month');
    let activeDaySelection = daySelect.getAttribute('data-day');

    let populateDays = (month) => {
      // delete the current set of <option> elements out of the
      // day <select>, ready for the next set to be injected
      while (daySelect.firstChild) {
        daySelect.removeChild(daySelect.firstChild);
      }

      // Create variable to hold new number of days to inject
      let dayNum;

      // 31 or 30 days?
      if (month === 'January' || month === 'March' || month === 'May' || month === 'July' || month === 'August' || month === 'October' || month === 'December') {
        dayNum = 31;
      } else if (month === 'April' || month === 'June' || month === 'September' || month === 'November') {
        dayNum = 30;
      } else {
        // If month is February, calculate whether it is a leap year or not
        let year = yearSelect.value;
        (year - 2016) % 4 === 0 ? (dayNum = 29) : (dayNum = 28);
      }

      // inject the right number of new <option> elements into the day <select>
      for (let i = 1; i <= dayNum; i++) {
        let option = document.createElement('option');
        option.textContent = i;
        daySelect.appendChild(option);
      }

      // if day has already been set, set daySelect's value
      // to that day, to avoid the day jumping back to 1 when you
      // change the year
      if (activeDaySelection) {
        daySelect.value = activeDaySelection;

        // If the previous day was set to a high number, say 31, and then
        // you chose a month with less total days in it (e.g. February),
        // this part of the code ensures that the highest day available
        // is selected, rather than showing a blank daySelect
        if (daySelect.value === '') {
          daySelect.value = activeDaySelection - 1;
        }

        if (daySelect.value === '') {
          daySelect.value = activeDaySelection - 2;
        }

        if (daySelect.value === '') {
          daySelect.value = activeDaySelection - 3;
        }
      }
    };

    let populateYears = () => {
      // get active year and clear attr value
      let selectedYear = yearSelect.getAttribute('data-year');
      yearSelect.setAttribute('data-year', '');

      // Make this year, and the 100 years before it available in the year <select>
      for (let i = 0; i <= settings.numberOfYears; i++) {
        let option = document.createElement('option');
        option.textContent = endYear - i;
        yearSelect.appendChild(option);
      }
      if (activeYearSelection != '') {
        yearSelect.value = activeYearSelection;
      }
    };

    let updateInput = () => {
      let day = daySelect.value;
      let month = new Date(Date.parse(monthSelect.value + ' 1, 2012')).getMonth() + 1;
      let year = yearSelect.value;

      dateInput.value = year.toString() + '-' + month.toString().padStart(2, '0') + '-' + day.toString().padStart(2, '0') + ' 00:00:00';
    };

    populateDays(monthSelect.value);
    populateYears();

    // when the month or year <select> values are changed, rerun populateDays()
    // in case the change affected the number of available days
    yearSelect.onchange = () => {
      populateDays(monthSelect.value);
      updateInput();
    };

    monthSelect.onchange = () => {
      populateDays(monthSelect.value);
      updateInput();
    };

    // update what day has been set to previously
    // see end of populateDays() for usage
    daySelect.onchange = () => {
      activeDaySelection = daySelect.value;
      updateInput();
    };
  }

  canAddBlocks(matrix) {
    if (isElement(matrix) && !matrix.hasAttribute('data-matrix-show-all-rows')) {
      let controls = matrix.querySelector('[data-matrix-controls]');
      if (controls) {
        if (matrix.hasAttribute('data-matrix-limit')) {
          let allBlocks = matrix.querySelectorAll('[data-matrix-block]');
          let allBlocksCount = allBlocks ? allBlocks.length : 0;
          let limit = parseInt(matrix.getAttribute('data-matrix-limit'));

          if (limit > allBlocksCount) {
            controls.classList.remove('hidden');
          } else {
            controls.classList.add('hidden');
          }
        } else {
          controls.classList.remove('hidden');
        }
      }
    }
  }

  showRowControls(block) {
    let row = block.closest('[data-matrix-row]');
    if (!row) return;
    let controls = row.querySelector('[data-matrix-controls]');
    if (!controls) return;
    controls.classList.remove('hidden');
  }

  hideRowControls(block) {
    let row = block.closest('[data-matrix-row]');
    if (!row) return;
    let controls = row.querySelector('[data-matrix-controls]');
    if (!controls) return;
    controls.classList.add('hidden');
  }

  matrixClickHandler(event) {
    let toggle = event.target;
    let matrix = toggle.closest(this.selectors.matrix);
    if (!matrix) return;
    if (toggle.classList.contains('is-submitting')) return;
    let showAllRows = matrix.hasAttribute('data-matrix-show-all-rows');
    let close = toggle.closest('[data-matrix-remove]');
    if (close) {
      event.preventDefault();
      let block = close.closest('[data-matrix-block]');
      if (showAllRows) {
        this.showRowControls(block);
      } else {
        this.canAddBlocks(matrix);
      }
      block.remove();
      return;
    }

    let add = toggle.closest('[data-matrix-new]');
    if (add) {
      event.preventDefault();
      toggle.classList.add('is-submitting');

      let allBlocks = matrix.querySelectorAll('[data-matrix-block]');

      let data = {
        name: 'matrixBlock',
        variables: {
          name: matrix.getAttribute('data-matrix'),
          type: add.getAttribute('data-matrix-new'),
          new: true,
          row: allBlocks ? parseInt(allBlocks.length) + 1 : 1,
        },
      };
      data[App.settings.csrfTokenName] = App.settings.csrfToken;

      axios
        .post('/?action=site/render/macro', data, {
          headers: {
            Accept: 'application/json',
          },
        })
        .then((response) => {
          let data = response.data;
          if (data.html) {
            let block = htmlToElement(data.html);
            if (showAllRows) {
              add.closest('[data-matrix-row]').appendChild(block);
              this.hideRowControls(block);
            } else {
              matrix.querySelector('[data-matrix-blocks]').appendChild(block);
              this.canAddBlocks(matrix);
            }
            this.init(block);
          }
        })
        .catch(() => {
          App.toasts.create({
            type: 'error',
            dismissable: true,
            message: 'Row Data Unavailable',
          });
        })
        .then(() => {
          toggle.classList.remove('is-submitting');
        });
    }
  }

  initTable(field) {
    let rowHolder = field.querySelector('[data-table-rows]');
    if (rowHolder && field.hasAttribute('data-table-sortable')) {
      Sortable.create(rowHolder, {
        draggable: '[data-table-row]',
        handle: '[data-table-sort-handle]',
        ghostClass: 'invisible',
      });
    }
    this.refreshTableControls(field);
  }

  refreshTableControls(table) {
    this.canAddTableRow(table);
    this.canSortTableRows(table);
    this.canDeleteTableRows(table);
  }

  canDeleteTableRows(table) {
    if (table.hasAttribute('data-table-minimum')) {
      let minimum = parseInt(table.getAttribute('data-table-minimum'));
      let allRows = table.querySelectorAll('[data-table-row]');
      let allRowsCount = allRows ? allRows.length : 0;
      if (allRowsCount <= minimum) {
        table.querySelectorAll('[data-table-remove-row]').forEach((remove) => {
          remove.classList.add('hidden');
        });
      } else {
        table.querySelectorAll('[data-table-remove-row]').forEach((remove) => {
          remove.classList.remove('hidden');
        });
      }
    }
  }

  canAddTableRow(table) {
    if (!(table || false)) return;
    let rowAddButton = table.querySelector('[data-table-add-row]');
    if (!rowAddButton) return;

    if (table.hasAttribute('data-table-limit')) {
      let allRows = table.querySelectorAll('[data-table-row]');
      let allRowsCount = allRows ? allRows.length : 0;
      let limit = parseInt(table.getAttribute('data-table-limit'));
      if (limit > allRowsCount) {
        rowAddButton.classList.remove('hidden');
      } else {
        rowAddButton.classList.add('hidden');
      }
    } else {
      rowAddButton.classList.remove('hidden');
    }
  }

  canSortTableRows(table) {
    if (!(table || false)) return;

    let rowSortButton = table.querySelector('[data-table-sort-rows]');
    if (!rowSortButton) return;

    let allRows = table.querySelectorAll('[data-table-row]');
    if (allRows.length > 1) {
      rowSortButton.classList.remove('hidden');
    } else {
      rowSortButton.classList.add('hidden');
    }
  }

  isTableSorting(table) {
    if (!(table || false)) return false;
    return table.classList.contains('is-sorting');
  }

  tableClickHandler(event) {
    let toggle = event.target;
    let table = toggle.closest(this.selectors.table);
    if (!table) return;
    let remove = toggle.closest('[data-table-remove-row]');
    if (remove) {
      event.preventDefault();
      let row = remove.closest('[data-table-row]');
      if (!row) return;
      row.remove();
      this.refreshTableControls(table);
      return;
    }

    let sort = toggle.closest('[data-table-sort-rows]');
    if (sort) {
      event.preventDefault();
      let table = sort.closest('[data-table]');
      if (!table) return;
      let handles = table.querySelectorAll('[data-table-sort-handle]');
      if (!handles || handles.length < 2) return;
      table.classList.toggle('is-sorting');
      handles.forEach((handle) => {
        handle.classList.toggle('hidden');
      });
      sort.textContent = this.isTableSorting(table) ? 'Done' : sort.getAttribute('data-original-text');
      return;
    }

    let add = toggle.closest('[data-table-add-row]');
    if (add) {
      event.preventDefault();
      toggle.classList.add('has-spinner');
      let allRows = table.querySelectorAll('[data-table-row]');
      let data = {
        name: '_macros/fields/table/rows',
        variables: {
          handle: table.getAttribute('data-table'),
          name: table.getAttribute('data-table-name') || table.getAttribute('data-table'),
          new: allRows ? parseInt(allRows.length) : 0,
        },
      };
      data[App.settings.csrfTokenName] = App.settings.csrfToken;

      axios
        .post('/?action=site/render/template', data, {
          headers: {
            Accept: 'application/json',
          },
        })
        .then((response) => {
          let data = response.data;
          if (data.html) {
            let row = htmlToElement(data.html);
            let handle = row.querySelector('[data-table-sort-handle]');
            if (handle && this.isTableSorting(table)) {
              handle.classList.remove('hidden');
            }
            table.querySelector('[data-table-rows]').appendChild(row);
            if (typeof window.placeholderShownPolyfill != 'undefined') {
              window.placeholderShownPolyfill.init(row);
            }
            this.refreshTableControls(table);
          }
        })
        .catch(() => {
          App.toasts.create({
            type: 'error',
            dismissable: true,
            message: 'Unexpected error',
          });
        })
        .then(() => {
          toggle.classList.remove('has-spinner');
        });
    }
  }

  initDatePickers(options = {}, context) {
    context = context || document;
    let settings = defaults(options, this.defaults.datePicker);
    let inputs = context.querySelectorAll(this.selectors.datePicker);
    inputs.forEach((input) => {
      this.initDatePicker(input, settings);
    });
  }

  initDateSelects(options = {}, context) {
    context = context || document;
    let settings = defaults(options, this.defaults.dateSelect);
    let fields = context.querySelectorAll(this.selectors.dateSelect);
    fields.forEach((field) => {
      this.initDateSelect(field, settings);
    });
  }

  initMatrix(context) {
    context = context || document;
    let fields = context.querySelectorAll(this.selectors.matrix);
    fields.forEach((field) => {
      this.canAddBlocks(field);
    });
    document.addEventListener('click', this.matrixClickHandler.bind(this), false);
  }

  initTables(options = {}, context) {
    context = context || document;
    let settings = defaults(options, this.defaults.table);
    let fields = context.querySelectorAll(this.selectors.table);
    fields.forEach((field) => {
      this.initTable(field, settings);
    });
    document.addEventListener('click', this.tableClickHandler.bind(this), false);
  }

  initAddressChoice(context) {
    context = context || document;

    let addressChoices = context.querySelectorAll('[data-address-choice]');
    addressChoices.forEach((addressChoice) => {
      let existingAddresses = addressChoice.querySelector('[data-existing-addresses]');
      let newAddress = addressChoice.querySelector('[data-new-address]');
      if (existingAddresses && newAddress) {
        let inputs = existingAddresses.querySelectorAll('input');
        inputs.forEach((input) => {
          input.addEventListener(
            'change',
            () => {
              if (input.value == 'new') {
                newAddress.classList.remove('hidden');
              } else {
                newAddress.classList.add('hidden');
              }
            },
            false,
          );
        });
      }
    });
  }

  checkboxSyncChangeHandler(event) {
    let checkbox = event.target.closest('[data-checkbox-sync]');
    if (!checkbox) return;
    let name = parseDataAttr(checkbox, 'data-checkbox-sync', '');
    let checkboxes = document.querySelectorAll('input[name="' + name + '"]');
    if (!checkboxes) return;
    checkboxes.forEach((_checkbox) => {
      _checkbox.checked = checkbox.checked;
    });
  }

  initCheckboxSync() {
    document.addEventListener('change', this.checkboxSyncChangeHandler.bind(this), false);
  }
}
